import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, Badge, Button, EmptyState } from '../components/design-system';
import { Activity, ChevronLeft, ChevronRight } from 'lucide-react';

export const ActivityPage = () => {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { loadActivity(); }, [page]);

  const loadActivity = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', '50');
      const res = await api.getActivity(params.toString());
      setActivity(res.activity || []);
      setTotal(res.total || 0);
      setTotalPages(res.totalPages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const actionBadgeVariant = (action) => {
    if (action.includes('created') || action.includes('registered')) return 'success';
    if (action.includes('deleted') || action.includes('deactivated')) return 'danger';
    if (action.includes('published')) return 'primary';
    if (action.includes('updated') || action.includes('approved')) return 'info';
    return 'default';
  };

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
        <p className="text-sm text-gray-500">{total} events logged</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>
      ) : activity.length === 0 ? (
        <EmptyState icon={Activity} title="No activity" description="User actions will be logged here." />
      ) : (
        <div className="space-y-1">
          {activity.map(a => (
            <Card key={a.id} className="!p-3">
              <div className="flex items-center gap-3">
                <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-medium text-gray-500">
                    {(a.display_name?.[0] || a.email?.[0] || 'S').toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm">
                    <span className="font-medium text-gray-900">{a.display_name || a.email || 'System'}</span>
                  </span>
                  <Badge variant={actionBadgeVariant(a.action)} className="ml-2">{a.action.replace(/_/g, ' ')}</Badge>
                  {a.resource_type && <span className="text-xs text-gray-400 ml-2">{a.resource_type} #{a.resource_id}</span>}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}
    </div>
  );
};
